/**
 * Phase 1.9 — Infrastructure Security v2 (Hybrid) Scoring Simulator.
 *
 * READ-ONLY. Does NOT persist, does NOT call any scoring RPC, does NOT
 * affect calculate_csv_domain_scores. Used solely by the Reviewer Console
 * "Infrastructure Security Scoring Preview" panel and offline validation.
 *
 * Model:
 *   v1_flat   — single AVG over all rated controls (matches today's behavior).
 *   v2_hybrid — controls grouped into modules:
 *                  B1..B4 (common / always in-scope)
 *                  C1..C8 (telecom-specific / in-scope iff component assessed)
 *               module_score   = AVG(rated controls in module)
 *               infra_score    = AVG(module_score for in-scope modules with data)
 *               (uniform module weighting — telecom-profile weighting is
 *                deferred to a later phase.)
 *
 * Status → numeric (0..2) mirrors the existing reviewer 0-2 scale standard.
 *   Implemented            → 2
 *   Partially Implemented  → 1
 *   Not Implemented        → 0
 *   Not Applicable / null  → excluded
 */

export type V1Status =
  | "Implemented"
  | "Partially Implemented"
  | "Not Implemented"
  | "Not Applicable"
  | null
  | undefined;

export interface SimRow {
  control_key: string;
  category: string;                 // catalog.category
  component_type?: string | null;   // catalog.component_type ("generic" or telecom kind)
  status: V1Status;
}

export interface SimScope {
  telecom_service_model: string[];
  components_present: string[];
  components_assessed: string[];
}

export type ModuleId =
  | "B1" | "B2" | "B3" | "B4"
  | "C1" | "C2" | "C3" | "C4" | "C5" | "C6" | "C7" | "C8";

export interface ModuleDef {
  id: ModuleId;
  label: string;
  kind: "common" | "telecom";
  /** Scope component label(s) that, when assessed, place this C-module in-scope. */
  scopeComponents?: string[];
}

export const V2_MODULES: ModuleDef[] = [
  { id: "B1", label: "Physical Security",                kind: "common" },
  { id: "B2", label: "Environmental Resilience",          kind: "common" },
  { id: "B3", label: "Monitoring & Operations",           kind: "common" },
  { id: "B4", label: "Network Resilience",                kind: "common" },
  { id: "C1", label: "Radio Access Network",              kind: "telecom", scopeComponents: ["Radio Access Network (RAN)", "Tower / Cell Site Infrastructure"] },
  { id: "C2", label: "Core Network",                       kind: "telecom", scopeComponents: ["Core Network"] },
  { id: "C3", label: "Signaling Network",                  kind: "telecom", scopeComponents: ["Signaling Network"] },
  { id: "C4", label: "Transport / Backhaul",               kind: "telecom", scopeComponents: ["Transport / Backhaul", "Fiber / Fixed Access Network"] },
  { id: "C5", label: "Virtualization & Edge",              kind: "telecom", scopeComponents: ["Cloud / Edge Infrastructure", "Data Centre / Co-location"] },
  { id: "C6", label: "OSS / BSS",                          kind: "telecom", scopeComponents: ["OSS / BSS"] },
  { id: "C7", label: "Roaming / Subscriber / IoT",         kind: "telecom", scopeComponents: ["Subscriber Data Stores", "IoT / M2M Platform", "Roaming / IPX"] },
  { id: "C8", label: "Service Continuity",                 kind: "telecom", scopeComponents: ["DR / Geo-Redundancy"] },
];

const MODULE_BY_ID: Record<ModuleId, ModuleDef> = Object.fromEntries(
  V2_MODULES.map((m) => [m.id, m]),
) as Record<ModuleId, ModuleDef>;

/** Map a control row to its v2 module via catalog category / component_type. */
export function controlToModule(row: SimRow): ModuleId | null {
  const cat = (row.category ?? "").trim();
  const ct = (row.component_type ?? "").trim();
  // B-block via category
  if (cat === "Physical Access Security") return "B1";
  if (cat === "Environmental Controls" || cat === "Power & Cooling Resilience") return "B2";
  if (cat === "Monitoring & Facilities Operations") return "B3";
  if (cat === "Network Infrastructure Protection") return "B4";
  // C-block via component_type
  switch (ct) {
    case "ran":         return "C1";
    case "core":        return "C2";
    case "signaling":   return "C3";
    case "transport":   return "C4";
    case "nfv":
    case "virtualization": return "C5";
    case "oss_bss":     return "C6";
    case "roaming":
    case "subscriber":
    case "iot":         return "C7";
    case "continuity":  return "C8";
    default:            return null;
  }
}

export function statusToScore(s: V1Status): number | null {
  switch (s) {
    case "Implemented":            return 2;
    case "Partially Implemented":  return 1;
    case "Not Implemented":        return 0;
    default:                       return null; // N/A or unrated
  }
}

export type BandLabel = "Minimal" | "Adequate" | "Strong" | "Extensive" | "Unrated";
/** Bands aligned with reviewer 0-2 scale: <0.5 Minimal · <1.25 Adequate · <1.75 Strong · ≥1.75 Extensive. */
export function bandFromAvg(avg: number | null): BandLabel {
  if (avg === null || !Number.isFinite(avg)) return "Unrated";
  if (avg < 0.5)  return "Minimal";
  if (avg < 1.25) return "Adequate";
  if (avg < 1.75) return "Strong";
  return "Extensive";
}

export interface ModuleBreakdown {
  id: ModuleId;
  label: string;
  kind: "common" | "telecom";
  rated: number;
  average: number | null;
  inScope: boolean;
  reasonExcluded: string | null;
}

export interface SimulationResult {
  v1: { average: number | null; band: BandLabel; ratedCount: number };
  v2: { average: number | null; band: BandLabel };
  delta: number | null;
  modules: ModuleBreakdown[];
  excluded: Array<{ id: ModuleId; label: string; reason: string }>;
}

/**
 * Run a simulation. Pure — no I/O. Caller supplies rows and scope.
 *
 * Scope rules for C-modules:
 *   • If any of the module's scopeComponents appears in components_assessed → in-scope.
 *   • Else if it appears in components_present but not assessed → excluded ("Present but not assessed").
 *   • Else → excluded ("Not present").
 *   • Fallback when scope is entirely empty: include any C-module that has rated controls
 *     (so the simulator still works on legacy assessments with no scope captured).
 */
export function simulateInfrastructureV2(
  rows: SimRow[],
  scope: SimScope,
): SimulationResult {
  // ── v1_flat: simple AVG over all rated controls ──
  const v1Scores: number[] = [];
  for (const r of rows) {
    const s = statusToScore(r.status);
    if (s !== null) v1Scores.push(s);
  }
  const v1Avg = v1Scores.length > 0
    ? v1Scores.reduce((a, b) => a + b, 0) / v1Scores.length
    : null;

  // ── Group rated controls by module ──
  const byModule = new Map<ModuleId, number[]>();
  for (const r of rows) {
    const mod = controlToModule(r);
    if (!mod) continue;
    const s = statusToScore(r.status);
    if (s === null) continue;
    if (!byModule.has(mod)) byModule.set(mod, []);
    byModule.get(mod)!.push(s);
  }

  const scopeEmpty =
    (scope.components_present ?? []).length === 0 &&
    (scope.components_assessed ?? []).length === 0;

  const breakdown: ModuleBreakdown[] = [];
  const excluded: Array<{ id: ModuleId; label: string; reason: string }> = [];

  for (const def of V2_MODULES) {
    const scores = byModule.get(def.id) ?? [];
    const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

    let inScope = true;
    let reason: string | null = null;
    if (def.kind === "telecom") {
      const components = def.scopeComponents ?? [];
      const assessedHit = components.some((c) => scope.components_assessed.includes(c));
      const presentHit  = components.some((c) => scope.components_present.includes(c));
      if (assessedHit) {
        inScope = true;
      } else if (presentHit) {
        inScope = false;
        reason = "Present but not assessed";
      } else if (scopeEmpty && scores.length > 0) {
        // Legacy fallback so the simulator still produces a number.
        inScope = true;
        reason = "Scope not declared — included via fallback (has rated controls)";
      } else {
        inScope = false;
        reason = "Not present";
      }
    }

    breakdown.push({
      id: def.id,
      label: def.label,
      kind: def.kind,
      rated: scores.length,
      average: avg,
      inScope,
      reasonExcluded: inScope ? null : reason,
    });

    if (!inScope) {
      excluded.push({ id: def.id, label: def.label, reason: reason ?? "Excluded" });
    }
  }

  // ── v2 score: AVG of in-scope modules that have data ──
  const inScopeAvgs = breakdown
    .filter((m) => m.inScope && m.average !== null && m.rated > 0)
    .map((m) => m.average!) as number[];
  const v2Avg = inScopeAvgs.length > 0
    ? inScopeAvgs.reduce((a, b) => a + b, 0) / inScopeAvgs.length
    : null;

  const delta = v1Avg !== null && v2Avg !== null ? v2Avg - v1Avg : null;

  return {
    v1: { average: v1Avg, band: bandFromAvg(v1Avg), ratedCount: v1Scores.length },
    v2: { average: v2Avg, band: bandFromAvg(v2Avg) },
    delta,
    modules: breakdown,
    excluded,
  };
}

/** Convenience: pretty-format an average to 2 decimals (or em-dash). */
export function fmtAvg(x: number | null | undefined): string {
  return x === null || x === undefined || !Number.isFinite(x) ? "—" : x.toFixed(2);
}

export { MODULE_BY_ID };
